import React, { useMemo, useState } from 'react';
import { Alert, Button, Checkbox, FormControlLabel, Snackbar } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { IconLogout } from '@tabler/icons-react';
import type { UserPreferencesPatch } from '@techgitter/contracts';
import { useRequiredCurrentUser } from '../contexts/CurrentUserContext';
import { usePrintLabelPrinters } from '../hooks/usePrintLabelPrinters';
import { parsePrinterTargetKey, printerTargetKey } from '../services/printerDefaults';
import { SmartSelect, type SmartSelectOption } from '../components/dropdown';
import { Chip } from '../components/chip';
import { getInitials, resolveAvatarColor } from '../styles/avatarPalette';
import { CHECKBOX_SX, OUTLINED_BTN_SX } from '../styles/designSystem';
import './UserSettingsPage.css';

type SnackState = { open: boolean; message: string; severity: 'success' | 'error' };

/** SmartSelect value for "no profile default" — the server's site default. */
const NO_PRINTER = '__site_default__';

/**
 * The signed-in user's own settings, opened by clicking their name in the
 * topbar. Roles are read-only here (an admin sets them in Admin → Users); the
 * preferences save as soon as they change, via `PATCH /api/me/preferences`.
 */
export const UserSettingsPage: React.FC = () => {
  const user = useRequiredCurrentUser();
  const { preferences, updatePreferences } = user;
  const { printers, isLoading: printersLoading, error: printersError } = usePrintLabelPrinters();
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<SnackState>({ open: false, message: '', severity: 'success' });
  const { instance } = useMsal();
  const [signingOut, setSigningOut] = useState(false);

  const avatarColor = resolveAvatarColor({ storedColor: user.avatarColor, seed: user.userId });

  const savedPrinter = preferences.defaultLabelPrinter;
  const printerOptions = useMemo<SmartSelectOption[]>(() => {
    const multiSite = new Set(printers.map((p) => p.siteId)).size > 1;
    const options: SmartSelectOption[] = [
      { value: NO_PRINTER, label: 'Site default printer' },
      ...printers.map((printer) => ({
        value: printerTargetKey(printer),
        label: multiSite ? `${printer.role} · ${printer.siteId}` : printer.role,
      })),
    ];
    // A saved lane that has left the fleet (or a list that failed to load)
    // still shows what is stored, rather than a blank field.
    if (savedPrinter && !options.some((o) => o.value === printerTargetKey(savedPrinter))) {
      options.push({ value: printerTargetKey(savedPrinter), label: `${savedPrinter.role} (saved)` });
    }
    return options;
  }, [printers, savedPrinter]);

  const save = async (patch: UserPreferencesPatch, message: string) => {
    setSaving(true);
    try {
      await updatePreferences(patch);
      setSnack({ open: true, message, severity: 'success' });
    } catch (err) {
      setSnack({
        open: true,
        message: err instanceof Error ? err.message : "Couldn't save your preferences",
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDebugPanelChange = (checked: boolean) =>
    save(
      { showDebugPanel: checked },
      checked ? 'Debug panel turned on' : 'Debug panel turned off',
    );

  const handleDebugPanelOnPhonesChange = (checked: boolean) =>
    save(
      { showDebugPanelOnPhones: checked },
      checked ? 'Debug panel will show on phones' : 'Debug panel hidden on phones',
    );

  const handlePrinterChange = (value: string | null) => {
    const target = value && value !== NO_PRINTER ? parsePrinterTargetKey(value) : null;
    save(
      { defaultLabelPrinter: target },
      target ? `Default label printer set to ${target.role}` : 'Default label printer cleared',
    );
  };

  // The MSAL cache is localStorage (authConfig.ts), so a session outlives the
  // browser closing — on a shared counter PC this is the only way to end it.
  // logoutRedirect clears the local cache *and* the Entra session, so the next
  // person can't simply click "Sign in" and land back in as this user.
  const handleSignOut = () => {
    setSigningOut(true);
    instance
      .logoutRedirect({
        account: instance.getActiveAccount() ?? instance.getAllAccounts()[0],
        postLogoutRedirectUri: window.location.origin,
      })
      .catch((err) => {
        setSigningOut(false);
        setSnack({
          open: true,
          message: err instanceof Error ? err.message : "Couldn't sign out",
          severity: 'error',
        });
      });
  };

  return (
    <div className="user-settings">
      <div className="user-settings__inner">
        <h1 className="user-settings__title">Settings</h1>

        <section className="user-settings__card" aria-labelledby="user-settings-profile">
          <div className="user-settings__identity">
            <span
              className="user-settings__avatar"
              aria-hidden="true"
              style={{ backgroundColor: avatarColor.bg, color: avatarColor.fg }}
            >
              {getInitials(user.displayName)}
            </span>
            <div className="user-settings__identity-text">
              <h2 id="user-settings-profile" className="user-settings__name">
                {user.displayName}
              </h2>
              <div className="user-settings__email">{user.email}</div>
            </div>
            <Button
              variant="outlined"
              size="small"
              startIcon={<IconLogout size={16} stroke={1.75} />}
              onClick={handleSignOut}
              disabled={signingOut}
              sx={{ ...OUTLINED_BTN_SX, ml: 'auto', flexShrink: 0 }}
            >
              Sign out
            </Button>
          </div>

          <div className="user-settings__fact">
            <div className="user-settings__fact-label">Roles</div>
            <div className="user-settings__roles">
              {user.roles.map((role) => (
                <Chip key={role} tone="info">
                  {role}
                </Chip>
              ))}
            </div>
            <p className="user-settings__hint">Roles are set by an administrator.</p>
          </div>
        </section>

        <section className="user-settings__card" aria-labelledby="user-settings-printing">
          <h2 id="user-settings-printing" className="user-settings__section-title">
            Printing
          </h2>
          <div className="user-settings__field">
            <SmartSelect
              label="Default label printer"
              value={savedPrinter ? printerTargetKey(savedPrinter) : NO_PRINTER}
              options={printerOptions}
              onChange={handlePrinterChange}
              placeholder={printersLoading ? 'Loading printers…' : 'Site default printer'}
              disabled={saving || printersLoading}
            />
          </div>
          <p className="user-settings__hint">
            {printersError
              ? 'Printer list unavailable right now.'
              : 'Preselected on the Print page when this browser has no printer saved for that label type.'}
          </p>
        </section>

        <section className="user-settings__card" aria-labelledby="user-settings-developer">
          <h2 id="user-settings-developer" className="user-settings__section-title">
            Developer
          </h2>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={preferences.showDebugPanel}
                onChange={(e) => handleDebugPanelChange(e.target.checked)}
                disabled={saving}
                sx={CHECKBOX_SX}
              />
            }
            label="Show debug panel"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={preferences.showDebugPanelOnPhones}
                onChange={(e) => handleDebugPanelOnPhonesChange(e.target.checked)}
                disabled={saving || !preferences.showDebugPanel}
                sx={CHECKBOX_SX}
              />
            }
            label="Also show on phones"
          />
          <p className="user-settings__hint">
            Adds the hook, cache, API-call and SignalR inspector to the bottom of the dashboard,
            calendar, customers and new-ticket pages. On screens under 900px wide it stays hidden
            unless "Also show on phones" is ticked.
          </p>
        </section>
      </div>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
};
